import { ButtonBase, styled } from '@mui/material';
import { SETTINGS_DROPDOWN_ITEM } from 'components/Header/HeaderDropdown';
import { useAuth } from 'contexts/auth';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Button, Drawer, Icon, IconButton, Logo, Stack, Text } from 'shell/components';
import { getSettingsPath, SETTINGS_SCOPE, SETTINGS_SECTION } from 'utils/settings';

const TimelyGraderLogo = () => {
    const history = useHistory();
    const { guard } = useNavigationGuard();

    return (
        <CustomButtonBase aria-label={'TimelyGrader'} onClick={() => guard(() => history.push('/'))}>
            <Logo />
        </CustomButtonBase>
    );
};

const Mobile = ({ hasCourseSelected, navOptions, dropdownOptions }) => {
    const { changeAccount } = useAuth();
    const history = useHistory();
    const location = useLocation();
    const { guard } = useNavigationGuard();

    const [open, setOpen] = useState(false);

    navOptions = [
        ...(navOptions || []).filter((navOption) => navOption.key !== 'more'),
        ...((navOptions || []).find((navOption) => navOption.key === 'more')?.options || []).map(({ key, value }) => {
            return { key, text: value, link: `/${key}` };
        }),
    ];

    const handleClick = (to) => {
        guard(() => {
            history.push(to);
            setOpen(false);
        });
    };

    const handleClickOption = ({ key }) => {
        if (key === SETTINGS_DROPDOWN_ITEM.PROFILE) {
            guard(() => {
                history.push(
                    getSettingsPath({
                        sectionId: SETTINGS_SECTION.EDIT_PROFILE,
                        scope: hasCourseSelected ? undefined : SETTINGS_SCOPE.ACCOUNT,
                    })
                );
                setOpen(false);
            });
        }
        if (key === SETTINGS_DROPDOWN_ITEM.COURSE_SETTINGS) {
            guard(() => {
                history.push(getSettingsPath({ sectionId: SETTINGS_SECTION.COURSE_DETAILS }));
                setOpen(false);
            });
        }
        if (key === SETTINGS_DROPDOWN_ITEM.PREFERENCES) {
            guard(() => {
                history.push(getSettingsPath({ sectionId: SETTINGS_SECTION.GRADING }));
                setOpen(false);
            });
        }
        if (key === SETTINGS_DROPDOWN_ITEM.SWITCH_COURSE) {
            guard(() => {
                changeAccount();
                setOpen(false);
            });
        }
        if (key === SETTINGS_DROPDOWN_ITEM.COURSE_DASHBOARD) {
            guard(() => {
                history.push('/');
                setOpen(false);
            });
        }
    };

    const renderNavOptions = () => {
        return navOptions.map((navOption) => {
            return (
                <Box key={navOption.key} sx={{ position: 'relative' }}>
                    <Button
                        sx={{ minWidth: 'unset' }}
                        color={location.pathname === navOption.link ? 'primary' : 'neutral'}
                        size={'large'}
                        startIcon={navOption.icon ? <Icon kind={navOption.icon} /> : null}
                        variant={'tertiary'}
                        onClick={() => handleClick(navOption.link)}
                    >
                        {navOption.text}
                    </Button>
                </Box>
            );
        });
    };

    const activeOption = navOptions.find(({ link }) => location.pathname.startsWith(link));

    const getPageHeader = () => {
        if (location.pathname.includes('/settings')) return 'Settings';
        return activeOption?.text;
    };

    return (
        <CustomStack alignItems={'center'} direction={'row'}>
            <TimelyGraderLogo />
            <Text variant={'p1'} weight={'semibold'}>
                {getPageHeader()}
            </Text>
            <IconButton
                aria-label={'navigation menu'}
                data-cy={'nav-hamburger-menu-button'}
                sx={{ mr: -2 }}
                kind={open ? 'exit' : 'hamburger'}
                size={'medium'}
                variant={'square'}
                onClick={() => setOpen((current) => !current)}
            />
            <Drawer device={'mobile'} open={open} onClose={() => setOpen(false)}>
                <Stack>
                    <IconButton
                        sx={{ alignSelf: 'flex-end' }}
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => setOpen(false)}
                    />
                    <Stack alignItems={'flex-start'} spacing={2}>
                        {renderNavOptions()}
                    </Stack>
                    <Stack
                        sx={{ position: 'absolute', bottom: (theme) => theme.spacing(5) }}
                        alignItems={'flex-start'}
                        spacing={2}
                    >
                        {dropdownOptions
                            .filter((option) => !['user', 'divider'].includes(option.key))
                            .map((option) => (
                                <Button
                                    key={option.key}
                                    color={'neutral'}
                                    size={'small'}
                                    variant={'tertiary'}
                                    onClick={() => handleClickOption(option)}
                                >
                                    {option.value}
                                </Button>
                            ))}
                    </Stack>
                </Stack>
            </Drawer>
        </CustomStack>
    );
};

const CustomButtonBase = styled(ButtonBase)(({ theme }) => {
    return {
        '&:focus-visible': {
            borderRadius: '6px',
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        justifyContent: 'space-between',
        width: '100%',
        height: '64px',
        padding: theme.spacing(0, 5),
    };
});

export default Mobile;
